// Developer memo:
// - Renderer may map visual colors, but must not mutate business data.
// - For ai_competition, prefer dataset-provided color, then model label color, then generic fallback.
// - Do not let generic palette overwrite explicit AI model colors.
import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import { aiModelColor, fallbackBackgroundColor, fallbackBorderColor } from './colors.js';

function formatLabelDate(value) {
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return value;
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
}

function axisTick(spec, value) {
    if (typeof value !== 'number') return value;
    if (spec.key === 'YTD') return `${(value * 100).toFixed(2)}%`;
    if (spec.y_format === 'percent_2') return `${value.toFixed(2)}%`;
    return Number.isInteger(value) ? value : value.toFixed(2);
}

function tooltipLabel(spec, context) {
    let label = context.dataset.label || '';
    if (label) label += ': ';
    const value = context.parsed.y;
    if (value !== null && value !== undefined) {
        label += axisTick(spec, value);
    }
    return label;
}

function chartBaseType(spec) {
    if (spec.chart_type === 'bar_stacked' || spec.chart_type === 'bar_signed') return 'bar';
    return 'line';
}

function normalizeDatasets(spec, payload) {
    return (payload.datasets || []).map((dataset, index) => ({
        ...dataset,
        backgroundColor: dataset.backgroundColor || fallbackBackgroundColor(index),
        borderColor: dataset.borderColor || fallbackBorderColor(index),
        borderWidth: dataset.borderWidth !== undefined ? dataset.borderWidth : 2,
        barThickness: spec.bar_thickness,
        fill: dataset.fill !== undefined ? dataset.fill : Boolean(spec.fill),
        spanGaps: true,
        pointRadius: dataset.type === 'bar' ? 0 : 2,
        pointHoverRadius: dataset.type === 'bar' ? 0 : 5,
    }));
}

function aiConfig(spec, payload) {
    return {
        type: 'line',
        data: {
            datasets: (payload.datasets || []).map((dataset, index) => {
                const label = dataset.label || 'Unknown Model';
                const borderColor = aiModelColor(label, index, dataset.borderColor);
                return {
                    ...dataset,
                    label,
                    data: dataset.data || [],
                    borderColor,
                    backgroundColor: dataset.backgroundColor || 'rgba(0, 0, 0, 0)',
                    pointBackgroundColor: dataset.pointBackgroundColor || borderColor,
                    pointBorderColor: dataset.pointBorderColor || borderColor,
                    fill: false,
                    tension: dataset.tension !== undefined ? dataset.tension : 0.1,
                    borderWidth: dataset.borderWidth !== undefined ? dataset.borderWidth : 1.5,
                    pointRadius: dataset.pointRadius !== undefined ? dataset.pointRadius : 1,
                    pointHoverRadius: dataset.pointHoverRadius !== undefined ? dataset.pointHoverRadius : 4,
                };
            }),
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        tooltipFormat: 'yyyy-MM-dd HH:mm',
                        unit: 'day',
                        stepSize: 1,
                        displayFormats: { day: 'MM/dd' },
                    },
                    grid: { display: false },
                    ticks: { autoSkip: true, maxRotation: 45, minRotation: 45 },
                },
                y: {
                    title: { display: Boolean(spec.y_axis_title), text: spec.y_axis_title || '', font: { size: 14 } },
                    ticks: { font: { size: 14, weight: 'bold' }, callback: (value) => axisTick(spec, value) },
                    grid: { display: true, color: 'rgba(0, 0, 0, 0.05)' },
                },
            },
            plugins: {
                title: { display: false },
                legend: { position: 'top', labels: { font: { size: 12 }, boxWidth: 20, padding: 10 } },
                decimation: { enabled: true, algorithm: 'lttb', samples: 35 },
                tooltip: { mode: 'index', intersect: false, callbacks: { label: (context) => tooltipLabel(spec, context) } },
            },
        },
    };
}

function defaultConfig(spec, payload) {
    const isStacked = spec.chart_type === 'bar_stacked';
    const isMultiAxis = spec.chart_type === 'multi_axis';
    const scales = {
        x: {
            type: 'category',
            ticks: { font: { size: 12 }, autoSkip: true, maxTicksLimit: 18, maxRotation: 45, minRotation: 0 },
            grid: { display: false },
            stacked: isStacked,
        },
    };

    if (isMultiAxis) {
        const titles = spec.y_axis_titles || [];
        scales['y-axis-price'] = {
            type: 'linear',
            position: 'left',
            title: { display: true, text: titles[0] || 'Price', font: { size: 14 } },
            ticks: { font: { size: 14, weight: 'bold' } },
        };
        scales['y-axis-volume'] = {
            type: 'linear',
            position: 'right',
            title: { display: true, text: titles[1] || 'Volume', font: { size: 14 } },
            ticks: { font: { size: 14, weight: 'bold' } },
            grid: { display: false },
        };
    } else {
        scales.y = {
            type: 'linear',
            title: { display: Boolean(spec.y_axis_title), text: spec.y_axis_title || '', font: { size: 14 } },
            ticks: { font: { size: 14, weight: 'bold' }, callback: (value) => axisTick(spec, value) },
            stacked: isStacked,
        };
    }

    const datasets = normalizeDatasets(spec, payload).map((dataset) => ({
        ...dataset,
        yAxisID: isMultiAxis ? (dataset.yAxisID || (dataset.type === 'bar' ? 'y-axis-volume' : 'y-axis-price')) : 'y',
    }));

    return {
        type: chartBaseType(spec),
        data: {
            labels: (payload.labels || []).map(formatLabelDate),
            datasets,
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'nearest', intersect: false },
            plugins: {
                title: { display: false },
                legend: { display: datasets.length > 1, position: 'top', labels: { font: { size: 12 } } },
                tooltip: { callbacks: { label: (context) => tooltipLabel(spec, context) } },
            },
            scales,
        },
    };
}

export function renderChart(canvasId, spec, payload) {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;

    const existingChart = Chart.getChart(canvasId);
    if (existingChart) existingChart.destroy();

    const config = spec.key === 'ai_competition' ? aiConfig(spec, payload) : defaultConfig(spec, payload);
    new Chart(canvas.getContext('2d'), config);
}
